<template>
  <ifield-b-modal
    id="the-indoor-building-detail"
    ref="modal"
    v-bind:title="$t('theIndoorBuildingDetail.indoorBuilding')"
    size="xl"
    centered
    no-body-border
    static
    modal-class="detail-dialog-static"
    v-on:show="$_onShowModal"
    v-on:hide="$_onHideModal"
  >
    <b-row v-if="isContentCreated" class="dialog-body">
      <b-col cols="6" class="dialog-body-left">
        <loading-mask v-bind:isLoading="isLoading">
          <div class="dialog-body-left-inner">
            <!-- 建物基本情報 -->
            <div class="dialog-info-title">
              <h3 class="title">
                {{ indoorBuildingName }}
              </h3>
              <p class="dialog-info-short-name">
                {{ indoorBuildingShortName }}
              </p>
            </div>
            <!-- データリスト -->
            <div class="dialog-info-data">
              <data-list v-bind:data="detailDataList" />
              <p class="dialog-info-remarks">
                {{ indoorBuildingRemarks }}
              </p>
            </div>
          </div>
        </loading-mask>
      </b-col>
      <b-col cols="6" class="dialog-body-right">
        <!-- 地図 -->
        <map-control
          ref="map-control"
          v-bind="$store.getters.mapControlBinds"
          v-bind:id-prefix="mapIdPrefix"
          v-bind:height="'520px'"
          v-bind:center="initialCenter"
          v-bind:zoom="18"
          v-bind:store-sync="['mapType']"
          v-bind:control="{
            header: false,
            layerMenu: false,
            indoorLevel: false,
            indoorBuilding: false,
          }"
          v-bind:can-change-indoor-level="false"
          v-bind:layer-renderers="layerRenderers"
          v-bind:reactive-render="isModalShown"
          v-on:map-control-initend="$_onMapInitEnd"
        />
      </b-col>
      <map-layer-controller
        v-bind:layer-render-parameters="layerRenderParameters"
        v-on:map-layer-renderers-created="$_onMapLayerRenderersCreated"
      />
    </b-row>

    <!-- modal footer -->
    <template v-slot:modal-footer="{ cancel }">
      <!-- 建物へ移動ボタン -->
      <b-button
        size="sm"
        variant="success"
        v-t="'theIndoorBuildingDetail.action.moveToIndoorBuilding'"
        v-on:click="$_moveToTheIndoorBuilding"
      />
      <!-- 閉じるボタン -->
      <b-button
        size="sm"
        variant="secondary"
        v-t="'theIndoorBuildingDetail.action.close'"
        v-on:click="cancel"
      />
    </template>
  </ifield-b-modal>
</template>

<script>
import DataList from '@/components/AppDataList.vue'
import MapLayerController from '@/components/map/AppMapLayerController.vue'
import { MapLayerMVTRenderer } from 'ifield-core-components'
import IFieldRasterTileRenderer from '@/js/map-renderer/IFieldRasterTileRenderer.js'
import BackdropRenderer from '@/js/map-renderer/BackdropRenderer.js'
import GeometryUtil from '@/js/util/GeometryUtil.js'
import { FUNCTION_TYPE, MAP_LAYER_REFERENCE_TABLE_TYPE } from '@/constants.js'
import { mapGetters } from 'vuex'
import _ from 'lodash'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'

const DATA_LIST_CENTROID_DIGITS = 6

export default {
  name: 'TheIndoorBuildingDetail',
  components: {
    'data-list': DataList,
    'map-layer-controller': MapLayerController,
  },
  props: {},
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      /**
       * 表示対象のプレイスID
       */
      indoorBuildingId: null,
      /**
       * 建物詳細データ
       *
       * API(/ifield/users/api/indoor_building/:id)のレスポンス
       * @type {Object}
       */
      data: null,
      /**
       * 初期中心座標
       * @type {Object}
       */
      initialCenter: this.$store.getters['map/center'],
      /**
       * レイヤー描画パラメータ
       * @type {Array.Object}
       */
      layerRenderParameters: [],
      /**
       * レイヤーレンダラー
       * @type {Array.Object}
       */
      layerRenderers: [],
      /**
       * ロード中の場合はtrue
       * @type {Boolean}
       */
      isLoading: false,
      /**
       * ダイアログの中身を生成済みの場合はtrue
       *
       * 地図を作り直さないため、一度開いたら閉じても保持する
       * @type {Boolean}
       */
      isContentCreated: false,
      /**
       * ダイアログを表示中の場合はtrue
       *
       * 非表示の間は地図のリアクティブな描画を止める
       * @type {Boolean}
       */
      isModalShown: false,
    }
  },
  computed: {
    /**
     * 地図要素のIDプレフィックス
     *
     * 同じ画面に本コンポーネントが複数載るため、インスタンスごとに一意にする
     * @type {String}
     */
    mapIdPrefix: function () {
      return `building-detail-${this._uid}-`
    },
    ...mapGetters('map', {
      /**
       * グローバルのレイヤー描画パラメータ
       * @type {Array.<Object>}
       */
      layerRenderParametersGlobal: 'layerRenderParameters',
    }),
    ...mapGetters('mapLayer', ['mapLayers']),
    /**
     * 表示範囲を指定する。
     * @type {Object}
     */
    bounds: function () {
      if (this.data == null || this.data.bounds_geom == null) {
        return null
      } else {
        return GeometryUtil.getBounds(this.data.bounds_geom).toObject()
      }
    },

    /**
     * 建物名を返す
     * @return {string}
     */
    indoorBuildingName: function () {
      if (!this.data) return ''

      return this.data.name
    },

    /**
     * 建物名(短縮名)を返す
     * @return {string}
     */
    indoorBuildingShortName: function () {
      if (!this.data) return ''

      return this.data.short_name
    },

    /**
     * 備考を返す
     * @return {string}
     */
    indoorBuildingRemarks: function () {
      if (!this.data) return ''

      return this.data.remarks
    },

    /**
     * データリストコンポーネント用のデータ
     * @type {object}
     */
    detailDataList: function () {
      return {
        standard: [
          // 現場
          {
            label: this.$t('theIndoorBuildingDetail.field.indoorSites'),
            value: this.indoorSiteNames.join(', '),
          },
          // 屋内レベル
          {
            label: this.$t('theIndoorBuildingDetail.field.indoorLevels'),
            value: this.indoorLevelNames.join(', '),
          },
          // 位置
          {
            label: this.$t('theIndoorBuildingDetail.field.geom'),
            value: this.latLngText,
          },
        ],
      }
    },

    /**
     * 緯度・経度を連結した文字列
     * @type {string}
     */
    latLngText: function () {
      if (this.data === null || this.data.bounds_geom == null) {
        return ''
      }

      const [lng, lat] = GeometryUtil.centroid(this.data.bounds_geom)

      // 指定した小数桁以降は表示しない
      const locationString = `${lat.toFixed(
        DATA_LIST_CENTROID_DIGITS
      )}, ${lng.toFixed(DATA_LIST_CENTROID_DIGITS)}`

      this.$log.debug(`lonlat = ${locationString}`)
      return locationString
    },

    /**
     * 現場名の配列
     * @type {string[]}
     */
    indoorSiteNames: function () {
      if (!this.data) return []
      return this.data.indoor_sites.map((site) => site.name)
    },

    /**
     * 屋内レベル名の配列
     * @type {string[]}
     */
    indoorLevelNames: function () {
      if (!this.data) return []
      return [...this.data.indoor_levels]
        .sort((a, b) => a.sort_order - b.sort_order)
        .map((level) => level.name)
    },
  },
  mounted: function () {
    this.$log.info('buildingDetail mounted')
  },
  methods: {
    /**
     * モーダルダイアログを表示する
     * @param {Object} recordId 表示対象のプレイスID
     */
    showModal: async function (recordId) {
      // データ初期化
      this.data = null
      this.indoorBuildingId = recordId

      this.$refs['modal'].show()
      try {
        await this.$_showIndoorBuilding()
      } catch {
        return
      }

      this.$_setLayerRenderParameters()
    },

    /**
     * モーダルダイアログを非表示にする
     */
    hideModal: function () {
      this.$refs['modal'].hide()
    },

    /**
     * モーダルダイアログを表示する時の処理
     */
    $_onShowModal: function () {
      // 地図を作り直さないよう、一度生成した中身は閉じても保持する
      this.isContentCreated = true
      this.isModalShown = true
    },

    /**
     * モーダルダイアログを非表示にする時の処理
     */
    $_onHideModal: function () {
      this.isModalShown = false
      // サニタイズ
      this.data = null
      this.indoorBuildingId = null

      this.$emit('hide-modal')
    },

    /**
     * レイヤー描画パラメータを設定する
     */
    $_setLayerRenderParameters: function () {
      const params = _.cloneDeep(this.layerRenderParametersGlobal)
      for (const param of params) {
        switch (param.id) {
          case MapLayerMVTRenderer.LAYER_ID():
            this.$_setMapLayerRenderParameters(param)
            break
          case IFieldRasterTileRenderer.LAYER_ID:
            param.style = { ...param.style, indoorLevelId: null }
            break
          case BackdropRenderer.LAYER_ID:
            param.visible = false
            break
        }
      }
      this.layerRenderParameters = params
    },

    /**
     * マップレイヤーの描画パラメータを設定する
     * @param {Object} param 描画パラメータ
     */
    $_setMapLayerRenderParameters: function (param) {
      for (const subParam of param.subLayers) {
        const layer = this.mapLayers.find((l) => l.id === +subParam.id)
        if (!layer) continue

        if (
          layer.reference_table_type ===
          MAP_LAYER_REFERENCE_TABLE_TYPE.INDOOR_SITE
        ) {
          // 強制表示
          subParam.visible = { geometry: true, label: true }
          // 対象の現場のみにフィルタ
          const siteIds =
            (this.data.indoor_sites &&
              this.data.indoor_sites.map((s) => s.id)) ||
            []
          const siteParam = { indoor_site_id: siteIds }
          if (subParam.filter) {
            subParam.filter = { ...subParam.filter, ...siteParam }
          } else {
            subParam.filter = siteParam
          }
        } else if (
          layer.reference_table_type ===
          MAP_LAYER_REFERENCE_TABLE_TYPE.INDOOR_BUILDING
        ) {
          // 強制表示
          subParam.visible = { geometry: true, label: true }
          // 対象の建物のみにフィルタ
          const buildingParam = { indoor_building_id: [this.indoorBuildingId] }
          if (subParam.filter) {
            subParam.filter = { ...subParam.filter, ...buildingParam }
          } else {
            subParam.filter = buildingParam
          }
        } else if (layer.reference_table_type === +FUNCTION_TYPE.PLACE) {
          // グローバル表示
          const levelParam = { indoor_level_id: null }
          if (subParam.filter) {
            subParam.filter = { ...subParam.filter, ...levelParam }
          } else {
            subParam.filter = levelParam
          }
        }
      }
    },

    /**
     * レンダラー作成完了時処理
     * @param {Array.<Object>} renderers 作成されたレンダラー配列
     */
    $_onMapLayerRenderersCreated: function (renderers) {
      this.layerRenderers = renderers.map((r) => r.render)
    },

    /**
     * 地図初期化完了時処理
     */
    $_onMapInitEnd: function () {
      this.map = this.$refs['map-control']
      this.bounds && this.map.fitBounds(this.bounds)
    },

    /**
     * 建物情報を表示する
     */
    $_showIndoorBuilding: async function () {
      if (!this.indoorBuildingId) {
        return
      }

      this.$_showLoading()

      await this.$_updateMapLayers()

      try {
        // APIを経由して建物情報を取得する
        this.data = await this.$store.dispatch(
          'indoorBuilding/updateSpecificIndoorBuilding',
          this.indoorBuildingId
        )
      } catch (error) {
        this.handleApiError(error)
        this.$_hideLoading()
        throw error
      }

      // マップが読み込まれている場合は、マップの表示範囲を移動
      this.map && this.map.fitBounds(this.bounds)

      this.$_hideLoading()
    },

    /**
     * マップレイヤー更新
     */
    $_updateMapLayers: async function () {
      try {
        await this.$store.dispatch('mapLayer/updateMapLayersWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch maplayers. use fetched before.')
        })
      }
    },

    /**
     * 建物へ移動するボタンを押したときの処理
     */
    $_moveToTheIndoorBuilding: function () {
      this.$emit('indoor-building-fit-map', this.bounds)
      this.hideModal()
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
.dialog-body {
  background-color: #ffffff;
  display: flex;
  height: 520px;
  margin: 0;
  width: 100%;

  ::v-deep .dialog-info-card {
    padding: 0;
  }
}

.dialog-body-left {
  padding-left: 0;
  height: 100%;
}

.dialog-body-left-inner {
  background-color: #f0f0f0;
  border: 1px solid #f0f0f0;
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 20px;
  text-align: left;
  @include mixin-scrollbar;
}

.dialog-info-title {
  margin: 0 0 20px;

  .title {
    font-size: $font-size-large;
    font-weight: $font-weight-bold;
    margin: 0 0 10px;
  }
}

.dialog-info-short-name {
  color: $text-color-weak;
  font-size: $font-size-small;
}

.dialog-info-remarks {
  font-size: $font-size-small;
  margin: 0 0 20px;
  white-space: pre-line;
}

.dialog-body-right {
  padding-right: 0;
}
</style>
